import {
	Stack,
	Typography,
	Box,
	Divider,
	Button,
	CircularProgress,
} from "@mui/material";
import { FunctionComponent, useState } from "react";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import DeviceFormComponent from "../DeviceFormComponent";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import InstanceResponseContent from "../InstanceResponseContent";
import { v4 as uuidv4 } from "uuid";
import { CacheRefreshMessage } from "Infrastructure/Models/Messages/Messages";
import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useExportDevicesMutation } from "InstanceManagement/Api/Mutations/useExportDevicesMutation";
import { BlTooltip } from "Shared/BlTooltip";

interface Props {
	deviceStatuses: DeviceStatus[] | null;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

const FrequentActionsDialogContent: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, hubConnection, closeDialog } = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	const { data } = useUserInfoQuery();
	const isActionAccessible = (accessRight: string) =>
		data?.status === 200
			? data.data.accessRightCodes.some((x) => x === accessRight)
			: false;

	const exportDevicesMutation = useExportDevicesMutation();

	const [form, setForm] = useState<DeviceForm>({
		devices:
			deviceStatuses?.map((x) => ({
				name: x.deviceName,
				isChecked: false,
				isDisabled: x.deviceState !== DeviceState.Connected,
				deviceStatus: x,
			})) || [],
	});

	const handleRefreshCache = () => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		const selectedDevices = form.devices
			.filter((device) => device.isChecked)
			.map((device) => device.deviceStatus?.deviceName);

		if (selectedDevices.length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return;
		}

		const cacheRefreshMessage: CacheRefreshMessage = {
			filteredDeviceNames: selectedDevices,
			requestId: uuidv4(),
		};

		setMessageResponse({
			actionName: "Refresh cache",
			requestId: cacheRefreshMessage.requestId,
			deviceNames: selectedDevices,
		});

		hubConnection
			.invoke("cacheRefresh", cacheRefreshMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	const handleExport = () => {
		const selectedDevices = form.devices
			.filter((device) => device.isChecked)
			.map((device) => device.deviceStatus?.deviceName);

		exportDevicesMutation.mutate({
			filteredDevices: selectedDevices.length > 0 ? selectedDevices : [],
		});
	};

	if (messageResponse) {
		return (
			<InstanceResponseContent
				actionName={messageResponse.actionName}
				requestId={messageResponse.requestId}
				deviceNames={messageResponse.deviceNames}
				hubConnection={hubConnection}
				closeDialog={closeDialog}
			/>
		);
	}

	return (
		<Stack>
			{deviceStatuses && deviceStatuses.length > 0 ? (
				<>
					<Box mb={1}>
						<Typography variant="h6" component="div">
							Instance
						</Typography>
						<Divider />
						<Box ml={1.5} mt={1}>
							<DeviceFormComponent
								deviceStatuses={deviceStatuses}
								form={form}
								setForm={setForm}
							/>
						</Box>
					</Box>
					<Box>
						<Typography variant="h6" component="div">
							Akce
						</Typography>
						<Divider />
					</Box>
					<Stack
						direction="row"
						divider={<Divider orientation="vertical" flexItem />}
						spacing={2}
						ml={1.5}
						mt={2}
					>
						<BlTooltip
							title="Provede refresh cache na API vybraných instancí."
							enterDelay={1000}
						>
							<Button
								variant="outlined"
								color="primary"
								onClick={handleRefreshCache}
								disabled={!isActionAccessible("InstanceManagementRefreshCache")}
							>
								<CachedIcon />
								<Box ml={0.3}>Refresh cache</Box>
							</Button>
						</BlTooltip>

						<BlTooltip
							title="Vygeneruje Excel soubor s daty vybraných instancí. Pokud není vybrána žádná instance, stáhnou se data všech instancí (včetně uspaných s cached daty)."
							enterDelay={1000}
						>
							<Button
								variant="outlined"
								color="primary"
								onClick={handleExport}
								disabled={
									!isActionAccessible("InstanceManagement") ||
									exportDevicesMutation.isPending
								}
							>
								{exportDevicesMutation.isPending ? (
									<CircularProgress size={24} color="inherit" />
								) : (
									<FileDownloadIcon />
								)}
								<Box ml={0.3}>Export</Box>
							</Button>
						</BlTooltip>
					</Stack>
				</>
			) : (
				<Typography variant="caption">Žádné instance k dispozici</Typography>
			)}
		</Stack>
	);
};

export default FrequentActionsDialogContent;
